// Recovery for stale async-chunk references after a deploy.
//
// The app loads views/widgets lazily (React.lazy + import()), and async chunks
// are content-hashed (see webpack.config.js). When a new version is deployed,
// the previous build's chunk files are removed from the bucket (gsutil rsync
// -d). A client that is still running the old index.html then requests a chunk
// filename that no longer exists and gets a 404 — surfacing as a "ChunkLoadError"
// (there is no React error boundary around the lazy views, so it would
// otherwise crash the tree).
//
// This installs a global listener that detects such failures and reloads the
// page once, which fetches a fresh index.html referencing the current chunks.
// A short-lived sessionStorage guard prevents a reload loop if the failure is
// not actually caused by a stale deploy (e.g. offline, genuinely broken chunk).

const RELOAD_GUARD_KEY = 'chunkReloadAt';
// Suppress a second automatic reload within this window (ms) so a persistent
// failure surfaces normally instead of reloading forever.
const RELOAD_GUARD_MS = 10000;

function isChunkLoadError(err) {
  if (!err) {
    return false;
  }
  if (err.name === 'ChunkLoadError') {
    return true;
  }
  const message = typeof err === 'string' ? err : (err.message || '');
  return (
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

// sessionStorage can be unavailable or throw (e.g. Safari private mode), so all
// access goes through these guarded helpers.
function readReloadStamp() {
  try {
    return Number.parseInt(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || '0', 10) || 0;
  } catch (e) {
    console.debug('chunk-reload: sessionStorage read failed', e);
    return 0;
  }
}

function writeReloadStamp(value) {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(value));
  } catch (e) {
    console.debug('chunk-reload: sessionStorage write failed', e);
  }
}

function reloadOnce() {
  const now = Date.now();
  if (now - readReloadStamp() < RELOAD_GUARD_MS) {
    // Already reloaded very recently — assume reloading will not help and stop
    // to avoid an infinite loop.
    console.error('Chunk load failed again shortly after reload; not reloading again.');
    return;
  }

  writeReloadStamp(now);
  console.warn('Stale asset chunk detected (likely a new deploy); reloading.');
  window.location.reload();
}

// Installs global handlers for chunk load failures. Idempotent.
export function initChunkReloadHandler() {
  if (typeof window === 'undefined' || window.__chunkReloadHandlerInstalled) {
    return;
  }
  window.__chunkReloadHandlerInstalled = true;

  // import() rejections that nothing awaits surface here.
  window.addEventListener('unhandledrejection', event => {
    if (isChunkLoadError(event.reason)) {
      reloadOnce();
    }
  });

  // React re-throws uncaught render errors (e.g. from React.lazy with no error
  // boundary) to the browser, arriving as a window 'error' event.
  window.addEventListener('error', event => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      reloadOnce();
    }
  });
}
